//
// Shared "qualified teams" filter for a division's DivisionTeams.
//
// A team is QUALIFIED when it holds a capacity spot (secured OR waitlisted) — i.e.
// it has at least a partial payment, or the division is free. Unpaid teams in a
// paid division are `pending` and are NOT qualified. This mirrors what
// /admin/organize shows (it hides `pending`).
//
// The capacity/payment signals live on RegistrationTeams. DivisionTeams reference
// their RegistrationTeam via `divisionTeam.registrationTeam`, so callers pass the
// division's DivisionTeams plus the division *stats* (from
// GET /registrationTeams/division/:id/stats) which carry the enriched
// RegistrationTeams (`paymentReceived`/`isFree`) + effective `config`.

import getObjId from './getObjId';
import { getCapacityState, getRegistrationTeamsForDivision, CapacityState } from './waitlist';
import { isAccepted, isWithdrawn } from './registrationTeamStatus';

/** Division stats payload (GET /registrationTeams/division/:id/stats) — dynamic API shape. */
interface DivisionStatsLike {
  config?: Record<string, any>;
  teams?: any[];
  [key: string]: any;
}

export interface DivisionTeamState {
  team: any;
  state: CapacityState;
  rt: any;
}

/**
 * Map each DivisionTeam to its capacity state ('secured' | 'waitlisted' |
 * 'pending') using the division stats. A DivisionTeam with no matching
 * RegistrationTeam (e.g. manually-created teams, test teams filtered out of
 * stats) or when stats haven't loaded yet defaults to 'secured' so it stays
 * visible rather than being hidden prematurely.
 */
export function deriveDivisionTeamStates(
  divisionTeams: any[] = [],
  divisionStats?: DivisionStatsLike | null,
  divisionId?: string,
): DivisionTeamState[] {
  const config = divisionStats && divisionStats.config;
  // Manually-added teams (active, no RT link) consume seats (ADR 0056). Prefer
  // the server's count from the stats payload; fall back to counting them in
  // the DT list we already hold so linked RTs derive against the true
  // remaining capacity even with a stale/older stats shape.
  const manualSeats = Number.isFinite(Number(divisionStats && divisionStats.manualSeats))
    ? Number(divisionStats && divisionStats.manualSeats)
    : (divisionTeams || []).filter((t) => t && t.active && !getObjId(t.registrationTeam)).length;
  const wlDivision = config ? { _id: divisionId, config, manualSeats } : null;
  const wlRegTeams = wlDivision
    ? getRegistrationTeamsForDivision(divisionId, (divisionStats && divisionStats.teams) || [])
    : [];
  return (divisionTeams || []).map((team) => {
    const rtId = getObjId(team && team.registrationTeam);
    const rt = rtId != null
      ? wlRegTeams.find((r) => String(getObjId(r)) === String(rtId))
      : null;
    const state: CapacityState = (rt && wlDivision) ? getCapacityState(rt, wlRegTeams, wlDivision) : 'secured';
    return { team, state, rt };
  });
}

/**
 * The qualified (non-`pending`) DivisionTeams for a division.
 *
 * Exception: an admin-ACCEPTED team (officially placed, e.g. force-added with
 * payment/waiver handled on paper) is always kept, even while capacity-pending
 * (unpaid). WITHDRAWN teams are dropped unconditionally — the accepted escape
 * hatch must not resurrect a team that has pulled out (a team can be both, if
 * it was accepted first and withdrew later).
 */
export function filterQualifiedDivisionTeams(
  divisionTeams: any[] = [],
  divisionStats?: DivisionStatsLike | null,
  divisionId?: string,
): any[] {
  return deriveDivisionTeamStates(divisionTeams, divisionStats, divisionId)
    .filter((x) => !isWithdrawn(x.rt))
    .filter((x) => x.state !== 'pending' || isAccepted(x.rt))
    .map((x) => x.team);
}
